import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';

interface Props {
  text: string;
}

const MESSAGE_CHANGE_INTERVAL_MS = 6000;

const STATUS_MESSAGES: Record<string, string[]> = {
  transport_searching: [
    'ご指定の条件を確認しています…',
    '出発地・到着地の情報を照合しています…',
    '利用可能な交通手段を照会しています…',
    '該当する便の空席状況を確認しています…',
    '所要時間を算出しています…',
    '料金条件を確認しています…',
    '移動時間と費用のバランスを比較しています…',
    '業務スケジュールに適した候補を抽出しています…',
    '最適な順に並べ替えています…',
    '候補を絞り込んでいます…',
    '表示内容を最終確認しています…'
  ],
  hotel_searching: [
    'ご指定の条件を確認しています…',
    '利用可能なホテルを照会しています…',
    '料金とプランを比較しています…'
  ]
};

const resolveMessage = (key: string, usedIndices: Set<number>): { message: string; index: number } => {
  const messages = STATUS_MESSAGES[key];
  if (!messages || messages.length === 0) {
    return { message: key, index: -1 };
  }

  const availableIndices = messages.map((_, i) => i).filter(i => !usedIndices.has(i));

  const candidates = availableIndices.length > 0 ? availableIndices : messages.map((_, i) => i);
  const randomIndex = candidates[Math.floor(Math.random() * candidates.length)];

  return { message: messages[randomIndex], index: randomIndex };
};

const OptimisticMessage = ({ text }: Props) => {
  const [currentMessage, setCurrentMessage] = useState('');
  const usedIndicesRef = useRef<Set<number>>(new Set());
  const isFirstRender = useRef(true);

  const styles = useMemo(() => getStyles(), []);

  const selectNewMessage = useCallback(() => {
    const { message, index } = resolveMessage(text, usedIndicesRef.current);
    if (index >= 0) {
      usedIndicesRef.current.add(index);
      const messages = STATUS_MESSAGES[text];
      if (messages && usedIndicesRef.current.size >= messages.length) {
        usedIndicesRef.current.clear();
        usedIndicesRef.current.add(index);
      }
    }
    return message;
  }, [text]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setCurrentMessage(selectNewMessage());
    }
  }, [selectNewMessage]);

  useEffect(() => {
    if (!currentMessage) return undefined;

    const timer = setInterval(() => {
      setCurrentMessage(selectNewMessage());
    }, MESSAGE_CHANGE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [currentMessage, selectNewMessage]);

  if (!currentMessage) return null;

  return (
    <>
      <style>{keyframesStyle}</style>
      <div style={styles.container}>
        <span style={styles.shimmerText}>{currentMessage}</span>
      </div>
    </>
  );
};

const keyframesStyle = `
  @keyframes optimistic-message-shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }
`;

const getStyles = (): { container: CSSProperties; shimmerText: CSSProperties } => ({
  container: {
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'inline-block'
  },
  shimmerText: {
    color: '#666666',
    background:
      'linear-gradient(90deg, #666666 0%, #666666 35%, #aaaaaa 45%, #cccccc 50%, #aaaaaa 55%, #666666 65%, #666666 100%)',
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'optimistic-message-shimmer 2s infinite linear'
  }
});

export default OptimisticMessage;
